import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { useClub } from '@/contexts/ClubContext';
import { billingService, memberService } from '@/services/storageService';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Check, Crown, Sparkles, Star } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import type { ApiClubMember } from '@/types';
import { useI18n } from '@/contexts/I18nContext';

type Plan = {
  id: 'package1' | 'package2' | 'package3';
  name: string;
  tier: string;
  members: string;
  monthlyPrice: number;
  yearlyDiscountPct: number;
  accent: string;
  icon: React.ReactNode;
  features: string[];
};

export default function PackagesPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { activeClubId, currentClubRole } = useClub();
  const clubId = activeClubId ? Number(activeClubId) : null;

  const PLANS: Plan[] = [
    {
      id: 'package1',
      name: t('pkg_name_starter', 'Starter'),
      tier: t('pkg_tier_1', 'Package 1'),
      members: t('pkg_feature_50_members', 'Up to 50 active members'),
      monthlyPrice: 69,
      yearlyDiscountPct: 5,
      accent: 'border-slate-300 dark:border-slate-700',
      icon: <Star size={16} />,
      features: [
        t('pkg_feature_member_mgmt', 'Member management'),
        t('pkg_feature_queue_courts', 'Queue and court assignment'),
        t('pkg_feature_basic_leaderboard', 'Match history and basic leaderboard'),
        t('pkg_feature_basic_dashboard', 'Basic admin dashboard'),
      ],
    },
    {
      id: 'package2',
      name: t('pkg_name_pro', 'Pro'),
      tier: t('pkg_tier_2', 'Package 2'),
      members: t('pkg_feature_150_members', 'Up to 150 active members'),
      monthlyPrice: 159,
      yearlyDiscountPct: 8,
      accent: 'border-primary/40',
      icon: <Sparkles size={16} />,
      features: [
        t('pkg_feature_everything_starter', 'Everything in Starter'),
        t('pkg_feature_coach_eval', 'Coach evaluations'),
        t('pkg_feature_equipment', 'Equipment tracking'),
        t('pkg_feature_ml_limited', 'Limited ML-assisted analytics for key performance trends'),
        t('pkg_feature_dashboard_insights', 'Actionable performance insights on dashboard'),
        t('pkg_feature_daily_advanced', 'Daily match reports and advanced leaderboard trends'),
      ],
    },
    {
      id: 'package3',
      name: t('pkg_name_elite', 'Elite'),
      tier: t('pkg_tier_3', 'Package 3'),
      members: t('pkg_feature_250_members', 'Up to 250 active members'),
      monthlyPrice: 299,
      yearlyDiscountPct: 10,
      accent: 'border-amber-400/50',
      icon: <Crown size={16} />,
      features: [
        t('pkg_feature_everything_pro', 'Everything in Pro'),
        t('pkg_feature_multi_admin', 'Advanced roles and permissions (multiple club admins)'),
        t('pkg_feature_ui_branding', 'Advanced UI controls (colors/fonts for club branding)'),
        t('pkg_feature_logo', 'Club logo upload/management'),
        t('pkg_feature_avatar', 'Member profile avatar selection'),
        t('pkg_feature_ai_chat', 'AI chat for match-history insights'),
        t('pkg_feature_dashboard_insights', 'Actionable performance insights on dashboard'),
      ],
    },
  ];

  const billingQuery = useQuery({
    queryKey: ['billing-status', clubId],
    queryFn: () => billingService.getStatus(clubId),
    enabled: Boolean(user && clubId),
  });

  const currentPackageCode = String(
    billingQuery.data?.package || billingQuery.data?.recommended_package || 'package1',
  )
    .trim()
    .toLowerCase();
  const isAdmin = String(currentClubRole || '').toLowerCase() === 'admin';

  const membersQuery = useQuery({
    queryKey: ['club-members', clubId],
    queryFn: () => memberService.list(clubId as number) as Promise<ApiClubMember[]>,
    enabled: Boolean(clubId),
  });

  const adminEmail =
    (membersQuery.data || []).find((m) => String(m.role || '').toUpperCase() === 'ADMIN')?.email || '';
  const mailtoAdmin = adminEmail
    ? `mailto:${encodeURIComponent(adminEmail)}?subject=${encodeURIComponent(t('pkg_upgrade_request_subject', 'Package Upgrade Request'))}`
    : '';



  return (
    <div className="w-full space-y-6 animate-fade-in px-3 sm:px-4 lg:px-6 overflow-x-hidden">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-3xl font-display font-bold text-foreground">{t('pkg_page_title', 'Packages')}</h1>
          <p className="text-muted-foreground mt-1">{t('pkg_page_subtitle', "Compare all plans and see your club's active package.")}</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className="text-sm px-3 py-1">
            {t('pkg_current', 'Current')}: {PLANS.find((p) => p.id === currentPackageCode)?.name || t('pkg_name_starter', 'Starter')} (
            {PLANS.find((p) => p.id === currentPackageCode)?.tier || t('pkg_tier_1', 'Package 1')})
          </Badge>
          {isAdmin ? (
            <Button onClick={() => navigate('/dashboard/club')}>{t('pkg_manage_subscription', 'Manage Subscription')}</Button>
          ) : adminEmail ? (
            <Button asChild variant="outline">
              <a href={mailtoAdmin}>{t('pkg_contact_admin', 'Contact Admin')}</a>
            </Button>
          ) : (
            <Button variant="outline" disabled>
              {t('pkg_contact_admin', 'Contact Admin')}
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        {PLANS.map((plan) => {
          const isCurrent = currentPackageCode === plan.id;
          return (
            <Card
              id={plan.id}
              key={plan.id}
              className={`relative flex h-full flex-col overflow-hidden border-2 ${plan.accent} ${
                isCurrent ? 'ring-2 ring-primary/40 shadow-lg' : ''
              }`}
            >
              <CardHeader className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="inline-flex items-center gap-2 font-bold text-muted-foreground">
                    {plan.icon}
                    {plan.tier}
                  </div>
                  {isCurrent ? <Badge>{t('pkg_current_plan', 'Current Plan')}</Badge> : null}
                </div>
                <CardTitle className="text-2xl">{plan.name}</CardTitle>
                <p className="text-sm text-muted-foreground">{plan.members}</p>
                <div className="rounded-xl border border-border bg-muted/30 px-3 py-2">
                  <p className="text-2xl font-bold text-foreground">
                    ${plan.monthlyPrice}
                    <span className="ml-1 text-sm font-medium text-muted-foreground">/{t('pkg_month', 'month')}</span>
                  </p>
                  <p className="text-xs text-muted-foreground">
                    ({plan.yearlyDiscountPct}% {t('pkg_yearly_savings', 'yearly savings')})
                  </p>
                </div>
              </CardHeader>
              <CardContent className="flex flex-1 flex-col">
                <div className="space-y-3">
                  {plan.features.filter((feature) => feature.trim().length > 0).map((feature, index) => (
                    <div key={`${plan.id}-${index}-${feature}`} className="flex items-start gap-2 text-sm">
                      <Check size={16} className="mt-0.5 text-primary shrink-0" />
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>
                <div className="mb-3"></div>
                {isAdmin ? (
                  <Button className="mt-auto w-full" onClick={() => navigate('/dashboard/club')}>
                    {isCurrent ? t('pkg_manage_current_plan', 'Manage Current Plan') : t('pkg_upgrade_to_this', 'Upgrade to This Plan')}
                  </Button>
                ) : (
                  <div className="mt-auto text-xs text-muted-foreground">{t('pkg_contact_admin_changes', 'Contact your club admin for plan changes.')}</div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="text-xs text-muted-foreground">
        {isAdmin ? (
          <>
            {t('pkg_need_billing_controls', 'Need billing controls? Open')}{' '}
            <Link className="underline" to="/dashboard/club">{t('nav_club', 'Club')}</Link>.
          </>
        ) : (
          t('pkg_billing_admin_only', 'Billing controls are available to club admins only.')
        )}
      </div>
    </div>
  );
}
